import type { Observation } from '@medplum/fhirtypes'
import type { SeenMedplumClient } from '@seen/core/client'
import {
  OBSERVATION_MODE_LATEST,
  type RecordedObservationAnswer,
} from '@seen/core/observationReferences'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'

interface FreezableAttrs {
  reference?: string | null
  references?: RecordedObservationAnswer[] | null
  frozenReference?: string | null
  frozenReferences?: RecordedObservationAnswer[] | null
}

// What freezing changes on a node: it shows the latest value, and what it
// recorded is set aside (out of `reference`/`references`) to go back to. A
// frozen node is never answered in the note, so it cannot be required.
export function frozenAttrs(
  attrs: FreezableAttrs
): Partial<ObservationNodeAttributes> {
  return {
    mode: OBSERVATION_MODE_LATEST,
    required: false,
    reference: null,
    references: null,
    frozenReference: attrs.reference ?? null,
    frozenReferences: attrs.references ?? null,
  }
}

// What unfreezing changes: capturing again, with the record set aside when
// it still stands (`keep`), else unanswered.
export function unfrozenAttrs(
  attrs: FreezableAttrs,
  keep: boolean
): Partial<ObservationNodeAttributes> {
  return {
    mode: null,
    reference: keep ? (attrs.frozenReference ?? null) : null,
    references: attrs.frozenReferences ?? null,
    frozenReference: null,
    frozenReferences: null,
  }
}

// Unfreezing as the set-aside record stands now, looked up so one deleted
// while frozen is not brought back.
export async function resolveUnfrozenAttrs(
  attrs: FreezableAttrs,
  medplum: SeenMedplumClient
): Promise<Partial<ObservationNodeAttributes>> {
  if (!attrs.frozenReference) {
    return unfrozenAttrs(attrs, false)
  }
  try {
    const record = await medplum.readReference<Observation>({
      reference: attrs.frozenReference,
    })
    return unfrozenAttrs(attrs, record.status !== 'entered-in-error')
  } catch {
    return unfrozenAttrs(attrs, false)
  }
}

// A panel keeps its answers in the instrument's own record, so freezing
// only sets that record's reference aside.
export function frozenPanelAttrs(
  panelReference: string | null | undefined
): Partial<ObservationNodeAttributes> {
  return {
    mode: OBSERVATION_MODE_LATEST,
    required: false,
    frozenReference: panelReference ?? null,
  }
}

export function unfrozenPanelAttrs(): Partial<ObservationNodeAttributes> {
  return { mode: null, frozenReference: null }
}
